import { useMutation } from '@apollo/client';
import ConfirmationModal from 'components/ConfirmationModal';
import { EmptyStateWithText } from 'components/EmptyState';
import AsideListItem from 'components/Layout/components/BaseDataAside/AsideListItem';
import { useContextBaseDataAside } from 'components/Layout/components/BaseDataAside/context';
import { BaseDataForm } from 'components/Layout/components/BaseDataAside/submenu';
import { TabPane } from 'components/Layout/components/common/styled-components';
import { useContextLayout } from 'components/Layout/context';
import { useContextSnackbar } from 'components/Snackbar/context';
import routePaths from 'constants/route-paths';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';
import { DoctorListData, DoctorPropType } from 'services/doctors';
import { DELETE_DOCTOR, RESTORE_DOCTOR } from 'services/mutation/doctors';
import { byLastName } from 'utils/sortBy';

const BaseDataDoctorsAsideMenu = () => {
  const { t } = useTranslation();
  const tabPaneRef = useRef(null);
  const { setActiveUser, setActiveMenu } = useContextBaseDataAside();
  const location = useLocation();
  const history = useHistory();
  const { filteredDoctors, setShouldRefetch } = useContextLayout();
  const { showApiError } = useContextSnackbar();

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorListData | undefined>();

  const [deleteDoctor] = useMutation(DELETE_DOCTOR);
  const [restoreDoctor] = useMutation(RESTORE_DOCTOR);

  const userPaths = matchPath(location.pathname, {
    path: routePaths.DATA_DOCTOR,
  }) as { params: { userId: string } };

  const pathName = location.pathname;

  useEffect(() => {
    const params = {
      userId: userPaths?.params?.userId,
    } as { userId: string };

    if (params.userId) {
      setActiveUser(params.userId);
      if (pathName.includes(routePaths.DATA_ACCESS)) {
        setActiveMenu(BaseDataForm.SYSTEM_ACCESS);
      } else if (pathName.includes(routePaths.DATA_CONTRACT)) {
        setActiveMenu(BaseDataForm.CONTRACT);
      } else {
        setActiveMenu(BaseDataForm.GENERAL);
      }
    }
  }, [setActiveMenu, setActiveUser, pathName, userPaths]);

  const onDeleteOrRestore = (doctor: DoctorListData) => {
    const name = `${doctor.name.title ? doctor.name.title + ' ' : ''}${doctor.name.firstFull} ${
      doctor.name.last
    }`;
    if (doctor.deletedAt) {
      setConfirmationText(t('doctor_restoreConfirmation', { name }));
    } else {
      setConfirmationText(t('doctor_deleteConfirmation', { name }));
    }
    setShowConfirmation(true);
    setSelectedDoctor(doctor);
  };

  const handleClose = (response: boolean) => {
    setShowConfirmation(false);
    if (!response || !selectedDoctor) {
      setSelectedDoctor(undefined);
      return;
    }
    if (!selectedDoctor.deletedAt) {
      deleteDoctor({ variables: { id: selectedDoctor.id } })
        .then(() => {
          setShouldRefetch('deleted');
          history.push(routePaths.BASE_DATA);
        })
        .catch(showApiError);
    } else {
      restoreDoctor({ variables: { id: selectedDoctor.id } })
        .then(() => {
          setShouldRefetch('restored');
          history.push(routePaths.BASE_DATA);
        })
        .catch(showApiError);
    }
  };

  return (
    <>
      <TabPane ref={tabPaneRef}>
        <div className="aside-menu">
          <ul className="menu-nav py-0">
            {filteredDoctors.length ? (
              filteredDoctors
                .sort(byLastName)
                .map((doctor: DoctorListData) => (
                  <AsideListItem
                    key={doctor.id}
                    id={doctor.id}
                    label={`${doctor.name.last}, ${
                      doctor.name.title ? doctor.name.title + ' ' : ''
                    }${doctor.name.firstFull}`}
                    description={(doctor as DoctorPropType)?.subjectArea || t('noSubjectAreaSet')}
                    gender={doctor.name.gender}
                    warning={1}
                    tabPaneRef={tabPaneRef}
                    routePath={routePaths.DATA_DOCTOR}
                    showDelete={true}
                    isDeleted={!!doctor.deletedAt}
                    onDelete={() => onDeleteOrRestore(doctor)}
                  />
                ))
            ) : (
              <EmptyStateWithText text={t('no_results_for_current_settings')} verticalPositionTop />
            )}
          </ul>
        </div>
      </TabPane>
      <ConfirmationModal show={showConfirmation} text={confirmationText} onResponse={handleClose} />
    </>
  );
};

export default BaseDataDoctorsAsideMenu;
